/**
 * What a kit remembers of its brand's WEBSITE: the look and the pictures.
 *
 * The kit's CSS facts (palette, fonts, radius) say what the brand is made of; they do not say what it
 * looks like. A page of the real site does. So a capture stores section crops of a full-page screenshot
 * (the LOOK, handed to the writer as reference images) and the real pictures the page ships (the
 * ASSETS, the only images a document of this brand may place). Stored as `profile.site`, client-owned
 * JSON like the references, URLs only, never bytes.
 *
 * Pure: shapes, readers, the prompt blocks and the allowlist strip. The capture pipeline (DOM, canvas,
 * network) lives in `captureKitSite.ts`.
 *
 * A kit can also hold pages of its brand's LAYOUT TEMPLATE (a PDF dropped on the kit). Those are a second,
 * opposite picture channel and their block lives here too, beside the site one, because the two sentences
 * have to be read together to stay non-contradictory: see `buildKitReferencePictures`.
 */
import { MAX_REFERENCE_IMAGES } from '../birtvyOpen/artifactReferenceImages'
import { isStoredImageUrl } from '../birtvyOpen/artifactValidate'
import type { MoodboardProfile } from '../types/presentation'
import type { SiteAssets } from '../style/styleSiteAssets'
import { ARTIFACT_TRACE_CONTRACT, describePageBox, type PageBox } from './artifactTraceContract'

export type KitSiteAssetRole = SiteAssets['images'][number]['role']

export interface KitSiteSection {
  id: string
  /** Our stored crop, root-relative. */
  url: string
  /** Where on the page the crop was taken, in screenshot pixels. */
  y: number
  height: number
  /** Dropped by the user: kept on the record (re-reading would only bring it back) but never used. */
  kept: boolean
}

export interface KitSiteAsset {
  id: string
  /** Our stored copy, root-relative. */
  url: string
  role: KitSiteAssetRole
  width?: number
  height?: number
  kept: boolean
}

export interface KitSiteCapture {
  url: string
  host: string
  at: number
  title?: string
  /** The full-page screenshot, root-relative, kept for a later re-cut. */
  screenshotUrl: string
  sections: KitSiteSection[]
  assets: KitSiteAsset[]
}

export const MAX_SITE_SECTIONS = 6
export const MAX_SITE_ASSETS = 12
/** Reference images per document. Four is the route's cap; three leaves room for the writer's own. */
export const MAX_SITE_REFERENCE_IMAGES = 3

export type ProfileWithSite = MoodboardProfile & { site?: KitSiteCapture }

const ROLES: ReadonlySet<string> = new Set(['hero', 'product', 'texture', 'other'])

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)

/** Defensive read: a malformed record answers null rather than a crash on the kit page. */
export function readSiteCapture(profile: MoodboardProfile | null | undefined): KitSiteCapture | null {
  const raw = (profile as ProfileWithSite | null | undefined)?.site
  if (!raw || typeof raw !== 'object') return null
  const url = str(raw.url)
  const screenshotUrl = str(raw.screenshotUrl)
  if (!url || !screenshotUrl) return null
  const sections: KitSiteSection[] = []
  for (const s of Array.isArray(raw.sections) ? raw.sections : []) {
    const sUrl = str((s as KitSiteSection)?.url)
    if (!sUrl || sections.length >= MAX_SITE_SECTIONS) continue
    sections.push({
      id: str((s as KitSiteSection).id) ?? `s${sections.length}`,
      url: sUrl,
      y: num((s as KitSiteSection).y) ?? 0,
      height: num((s as KitSiteSection).height) ?? 0,
      kept: (s as KitSiteSection).kept !== false,
    })
  }
  const assets: KitSiteAsset[] = []
  for (const a of Array.isArray(raw.assets) ? raw.assets : []) {
    const aUrl = str((a as KitSiteAsset)?.url)
    if (!aUrl || assets.length >= MAX_SITE_ASSETS) continue
    const role = str((a as KitSiteAsset).role)
    assets.push({
      id: str((a as KitSiteAsset).id) ?? `a${assets.length}`,
      url: aUrl,
      role: role && ROLES.has(role) ? (role as KitSiteAssetRole) : 'other',
      ...(num((a as KitSiteAsset).width) !== undefined ? { width: num((a as KitSiteAsset).width) } : {}),
      ...(num((a as KitSiteAsset).height) !== undefined ? { height: num((a as KitSiteAsset).height) } : {}),
      kept: (a as KitSiteAsset).kept !== false,
    })
  }
  return {
    url,
    host: str(raw.host) ?? hostOf(url),
    at: num(raw.at) ?? 0,
    ...(str(raw.title) ? { title: str(raw.title) } : {}),
    screenshotUrl,
    sections,
    assets,
  }
}

export function withSiteCapture(profile: MoodboardProfile, site: KitSiteCapture | null): MoodboardProfile {
  const rest: ProfileWithSite = { ...(profile as ProfileWithSite) }
  delete rest.site
  return site ? ({ ...rest, site } as MoodboardProfile) : (rest as MoodboardProfile)
}

/** A deck binding snapshots the kit; the site record is the live kit's business, like the references. */
export function stripSiteCapture(profile: MoodboardProfile): MoodboardProfile {
  return withSiteCapture(profile, null)
}

export function keptSections(site: KitSiteCapture | null | undefined): KitSiteSection[] {
  return site ? site.sections.filter((s) => s.kept) : []
}

export function keptAssets(site: KitSiteCapture | null | undefined): KitSiteAsset[] {
  return site ? site.assets.filter((a) => a.kept) : []
}

export function hostOf(url: string): string {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/**
 * A document runs in a sandboxed frame on an opaque origin, where a root-relative URL resolves to nothing.
 * Everything handed to the writer is therefore absolute against the app's own origin. Storage is public
 * (`/v1/storage/**` is permitAll), so the frame needs no cookie to load it.
 */
export function absoluteAssetUrl(url: string, origin: string): string {
  if (/^(https?:|data:)/i.test(url)) return url
  const base = origin.replace(/\/$/, '')
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`
}

/**
 * Which crops a given document looks at. The first crop (the top of the page: nav, hero, the brand's
 * loudest statement) always rides; the rest are spread by the document's index so nine references
 * between them see the whole page rather than all nine seeing the same fold.
 */
export function pickReferenceSections(
  site: KitSiteCapture | null | undefined,
  documentIndex: number,
  max = MAX_SITE_REFERENCE_IMAGES
): KitSiteSection[] {
  const kept = keptSections(site)
  if (kept.length <= max) return kept
  const rest = kept.slice(1)
  const step = Math.max(1, Math.floor(rest.length / (max - 1)))
  const offset = Math.abs(documentIndex) % rest.length
  const picked: KitSiteSection[] = [kept[0]]
  for (let k = 0; picked.length < max && k < rest.length; k += 1) {
    const candidate = rest[(offset + k * step) % rest.length]
    if (!picked.includes(candidate)) picked.push(candidate)
  }
  return picked
}

const sizeOf = (a: KitSiteAsset): string => (a.width && a.height ? `${a.width}x${a.height}` : 'size unknown')

/**
 * The KIT ASSETS block: the closed list of images a document of this brand may place. Named in the
 * contract as the one exception (beside verified logos) to "no images", so the wording here and the
 * contract's sentence must keep agreeing about the name.
 */
export function kitSiteAssetsPrompt(site: KitSiteCapture | null | undefined, origin: string): string {
  const assets = keptAssets(site)
  if (!site || assets.length === 0) return ''
  const lines = [
    `KIT ASSETS. Real pictures from ${site.host}, stored by this brand's kit. They are the ONLY images this document may use, referenced by EXACT URL:`,
    ...assets.map((a) => `- ${a.role}, ${sizeOf(a)}: ${absoluteAssetUrl(a.url, origin)}`),
    'Place one with <img src="…" style="object-fit:cover"> or background-image: url("…"), and give it a real role in the composition: a hero is worth a full-bleed background or half the canvas; a product shot sits in a frame with the theme\'s radius; a texture is a background, never content. Use an image only where the structure wants one; a document that needs none uses none. The list is closed: any other image URL does not exist, and the system removes every image that is not on it.',
  ]
  return lines.join('\n')
}

/**
 * What the writer is told the site crops are, next to the route's own REFERENCE IMAGES block.
 *
 * COLOUR WAS TAKEN OUT OF THIS SENTENCE (Guri, 2026-09-03) and the removal is the point. It used to ask
 * for "how the site uses colour blocks and dark bands", which on a real kit is a picture of a blue site
 * attached to an instruction to take its colour: bankofgeorgia.ge's kit holds nine curated colours led by
 * an orange, and its covers came out blue. A crop is the WORST colour evidence in the room anyway. It is
 * a JPEG of a rendered page, so it carries antialiasing, overlays, photography and whatever hero image
 * the marketing team shipped that week, while the theme context beside it carries hexes somebody chose.
 *
 * What a crop IS good for is everything a hex cannot say: how much air a section gets, how heavy the
 * chrome is, whether surfaces are flat or grained, how the page paces one block against the next, how the
 * brand photographs its subjects. That is what the sentence asks for now, and the palette line is stated
 * as a closed source rather than a preference, because a model looking at a blue page will otherwise read
 * silence as permission.
 *
 * THE COLOUR HALF IS UNTOUCHED WHEN TEMPLATE PAGES RIDE. Only the two clauses that name pictures change:
 * which of the attached images these are, and where the layout comes from. Without that, the crops' sentence
 * would claim the whole array and then forbid the very tracing the pages were attached to ask for.
 */
export function siteLookPrompt(
  host: string,
  family?: string,
  opts?: {
    /** How many TEMPLATE PAGES ride ahead of these crops in the same message. Zero keeps the sentence
     *  byte-identical to the one that shipped alone. */
    templatePages?: number
  }
): string {
  const pages = Math.max(0, opts?.templatePages ?? 0)
  const plural = pages === 1 ? '' : 's'
  // WHICH PICTURES, BY POSITION. A model sees one undifferentiated array of parts, so the only way to say
  // "these three, not those" is the order they were appended in, and `buildKitReferencePictures` is what
  // guarantees pages come first. No count is stated for the crops on purpose: crops are what the four-image
  // cap drops, so a number here could be a lie, while the pages ahead of them are never dropped.
  const lead =
    pages > 0
      ? `The attached images AFTER the ${pages} template page${plural} above are crops of ${host}, this brand's own website.`
      : `The REFERENCE IMAGES attached are pages of ${host}, this brand's own website.`
  const structure =
    pages > 0
      ? `this document's structure is the template page${pages === 1 ? "'s" : "s'"} above, not the website's`
      : family
        ? `this document's structure is the ${family} named above, not the page's`
        : "this document's structure is its own, not the page's"
  return `${lead} Take their SURFACE: density and air, chrome and frames, texture and grain, how one section is paced against the next, and how this brand photographs things. Take none of their colour: every colour in this document comes from the theme context above and from nowhere else, whatever the crops happen to show. Take none of their layout: ${structure}.`
}

/**
 * THE OPPOSITE INSTRUCTION: pages of the brand's own LAYOUT TEMPLATE, where the geometry is the whole point.
 *
 * A site crop and a template page are both pictures on one message and they ask for opposite things, which
 * is exactly why this is a second channel rather than a second sentence on `siteLook`. One array with one
 * prompt cannot carry "take none of their layout" and "trace their layout" without the model picking one at
 * random.
 *
 * The bullets are the "WHOSE COLOURS, WHOSE TYPE" rules of `ai/deckReconstructPrompt.ts`, which were written
 * and tested for the 1:1 reconstruct path, moved onto a page of a brand template. Two deliberate changes:
 *
 *  1. THE WORDS. Reconstruct says "GEOMETRY AND WORDS STAY TRACED"; a template page is furniture, not
 *     content, so the copy is REPLACED with this brand's own while the structure is kept. That is the second
 *     bullet below, and it is the only line where this block and the reconstruct rules disagree.
 *  2. PROPORTIONS, NOT PIXELS. Reconstruct traces a slide screenshot onto the same 1920x1080 frame it came
 *     from; a template page is A4 or worse, so a literal coordinate read is a portrait layout squeezed onto
 *     a landscape canvas. Ratios transfer, pixels do not.
 *
 * Colour is stated the same way `siteLookPrompt` states it (Guri, 2026-09-03, after bankofgeorgia.ge covers
 * came out blue): the theme is a closed source, not a preference, because a model looking at a coloured page
 * reads silence as permission.
 */
export function templatePagePrompt(args: {
  /** How many pages ride. One per role is the normal case. */
  pageCount: number
  /** Site crops riding after them, which only changes how the pages are pointed at. */
  siteCrops?: number
  /** What the set is called when the kit has a name for it ("SPACE10"). */
  templateName?: string
  /** The role this document answers, so the page reads as "how this template does THIS job". */
  roleLabel?: string
  /**
   * WHICH OF THE TWO THINGS A PAGE CAN BE ASKED FOR. Default `restyle`, so every existing caller is
   * byte-identical; see {@link TemplatePageMode}.
   */
  mode?: TemplatePageMode
  /** Required by `trace` and ignored by `restyle`: the page's real size and the box it lands in. */
  trace?: TracePageFacts
}): string {
  if (args.mode === 'trace') return tracePagePrompt(args.trace, args.templateName)
  const n = Math.max(1, Math.floor(args.pageCount))
  const s = n === 1 ? '' : 's'
  const isAre = n === 1 ? 'is' : 'are'
  const itThey = n === 1 ? 'it is' : 'they are'
  const count = n === 1 ? '' : `${n} `
  const lead = (args.siteCrops ?? 0) > 0 ? `The FIRST ${count}attached image${s}` : `The ${count}attached image${s}`
  const source = args.templateName?.trim() || "this brand's own layout template"
  const job = args.roleLabel?.trim() ? `, the page${s} that does the ${args.roleLabel.trim()} job` : ''
  return [
    `TEMPLATE PAGE${s.toUpperCase()}. ${lead} ${isAre} ${n === 1 ? 'a page' : 'pages'} of ${source}${job}, and ${itThey} the LAYOUT this document lands on. Not a mood and not a subject: the arrangement.`,
    '- TRACE THE GEOMETRY. Same blocks, same order, same relative sizes, same air between them: where the headline sits in the field, how many columns the body runs in, where the rule or the divider goes, which corner carries the small print, how far the type scale jumps from one level to the next. Read PROPORTIONS, not pixels — a template page is not 1920x1080, so map what you read onto this canvas and keep the ratios rather than the coordinates.',
    '- REPLACE THE WORDS, AND THIS IS THE ONE LINE WHERE A TEMPLATE PAGE IS NOT A TRACE. The copy is not transcribed. Every line is written fresh, for this brand and this brief, at the LENGTH the page gives it: a two-word label stays two words, a forty-word paragraph stays about forty, a three-item list stays three items. Never lift a sentence off the page, never name the brand printed on it, never mention that a page was attached.',
    "- ONLY THE PAINT CHANGES. Background, text, accent and border come from the theme context above and from nowhere else, and the font family comes with them. Map by ROLE, not by hue: the page's field becomes this theme's background, its body text becomes this theme's text colour, the one colour it reserves for emphasis becomes this theme's accent. Do not recolour everything the accent.",
    "- WHERE THE PAGE PUTS ITS MARK this document puts this brand's, at that size and in that position, and nowhere at all if the page carries none.",
    '- WHERE THE PAGE PUTS A PHOTOGRAPH this document puts a KIT ASSET if one fits the slot, and otherwise leaves the block as a plain field of theme colour at the same size. Nothing from the page is embedded or linked, and no image is invented to fill a hole.',
    '- NEW WORDS ARE NOT PERMISSION TO REBUILD. If you find yourself moving a block, dropping a column, adding a section the page does not have, or recentring something the page sets off-centre, you have left the page and gone back to composing.',
    "- NEVER HALF-RESTYLE. Either every colour in this document is the theme's, or the document is wrong. A page that keeps three of the template's hexes reads as a mistake, because it is one.",
  ].join('\n')
}

/**
 * THE TWO THINGS A TEMPLATE PAGE CAN BE ASKED FOR, and both are wanted.
 *
 * `restyle` is what shipped and what every composition tile still uses: keep the page's arrangement, write
 * this brand's own fresh copy into it, repaint it in the theme. It is the right answer when the output is a
 * NEW artifact that should look like it belongs in this template.
 *
 * `trace` is what "exactly" means. Guri, 2026-09-04, after being told twice this already worked: "when I
 * upload it, it should do exactly one-to-one, like a reference. It should bring exactly that one from the
 * PDF." The restyle rule's second bullet says, in so many words, that the copy is NOT transcribed, so the
 * feature he was pointed at is the one thing he was not asking for. A mode rather than a rewrite, because
 * both jobs are real and the restyle one is what twenty-one composition tiles are built on.
 *
 * THE COLOUR RULE IS NOT LOOSENED BY THIS. `restyle` still ends "either every colour in this document is
 * the theme's, or the document is wrong", because a half-restyle that keeps three of the template's hexes
 * reads as a mistake. A trace keeping the page's own hexes is not that bug wearing a flag: it is a copy of
 * a page, so the page's colours ARE its colours, and it takes none from the theme at all. Two closed
 * sources, never a blend, which is the same shape the rule always had.
 */
export type TemplatePageMode = 'restyle' | 'trace'

/** What a trace needs to know about the page it is copying, beyond the picture itself. */
export interface TracePageFacts {
  /** The render's real pixel size, measured off the image. Without it the model guesses the aspect and
   *  the page comes back stretched, which is exactly the failure being reported. */
  imageWidth: number
  imageHeight: number
  /** Where that page sits inside the 1920x1080 stage, from `pageBoxFor`. */
  box: PageBox
  /** The page's own number in the source document, for the sentence and the manifest. */
  pageNumber?: number
  /** The brand's real faces, when the kit knows them. A substitute face wider than the page's own is what
   *  makes a traced run overflow its box, so naming the real one is worth the two lines. */
  headingFont?: string
  bodyFont?: string
  /** The kit's own mark, and the name to put in its alt. Absent means the page's wordmark is traced as
   *  type or as a placeholder, never as an invented URL. */
  mark?: { url: string; brandName: string }
}

/**
 * THE TRACE INSTRUCTION: the whole contract plus this page's own facts.
 *
 * ONE BLOCK, NOT TWO. An earlier shape had the rules in one place and the page facts in another, and the
 * two drifted immediately: the rules said "the request states the page's real pixel size" while the facts
 * block was optional. Here the facts are inside the same text, so a caller that forgets them produces a
 * prompt that visibly has no PAGE block rather than one that quietly lies about having one.
 *
 * NO FACTS IS STILL A LEGAL TRACE, and it says so out loud rather than pretending: a page whose render
 * could not be measured is traced onto the full stage, which is right for a landscape deck page and wrong
 * for A4, and the flag on the stored record is what tells the user which they got.
 */
export function tracePagePrompt(facts: TracePageFacts | undefined, templateName?: string): string {
  const source = templateName?.trim() || "this brand's own document"
  const which =
    facts?.pageNumber !== undefined ? `page ${facts.pageNumber} of ${source}` : `a page of ${source}`
  const parts = [
    ARTIFACT_TRACE_CONTRACT,
    `TRACE THIS PAGE. The attached image is ${which}, a real page this brand already has. Reproduce it one to one.`,
  ]
  parts.push(
    facts
      ? `THE PAGE\n${describePageBox(facts.imageWidth, facts.imageHeight, facts.box)}\nThe stage outside that box takes the page's own background colour.`
      : "THE PAGE\nThe page's pixel size could not be measured. Read its proportions off the image itself, give it a box at that aspect ratio centred in the stage, and paint the stage around it in the page's own background colour. Do not stretch it to 16:9."
  )
  parts.push(
    'THE WORDS\nTranscribe them. Every headline, every paragraph, every caption, every label, every number, every footer line, every page number, exactly as printed. This is the whole point of this run. A document that comes back with better words than the page has is a FAILED trace, and so is one that comes back with fewer.'
  )
  parts.push(
    'THE PICTURES\nPlaceholder panels, per the rules. Name what each one showed in three words or fewer.'
  )
  if (facts?.mark) {
    parts.push(
      `THE MARK\nWhere this page prints its wordmark, use exactly:\n<img src="${facts.mark.url}" alt="${facts.mark.brandName}" style="position:absolute;left:NNpx;top:NNpx;width:NNpx;height:auto">\nat the traced position and size. If the page prints no wordmark, place none.`
    )
  }
  const faces = [facts?.headingFont?.trim(), facts?.bodyFont?.trim()].filter(Boolean) as string[]
  if (faces.length) {
    const named = faces.length === 2 && faces[0] !== faces[1] ? `${faces[0]} and ${faces[1]}` : faces[0]
    parts.push(`THE FACES\nThis template is set in ${named}. Use those, and load them from Google Fonts.`)
  }
  return parts.join('\n\n')
}

/** One picture channel: what the model looks at, and the sentence telling it why. */
export interface KitReferenceChannel {
  referenceImages: string[]
  prompt: string
}

/**
 * BOTH CHANNELS, BUILT TOGETHER, because building them apart is how they end up contradicting each other.
 *
 * The route takes four pictures (`MAX_REFERENCE_IMAGES`) and the two channels share that budget. TEMPLATE
 * PAGES RIDE FIRST AND SITE CROPS ARE DROPPED FIRST: a page is the instruction for this document's geometry,
 * so dropping it answers a question nobody asked, while a crop is ambient surface that degrades one picture
 * at a time. The normal run is one page plus three crops, which is exactly the cap.
 *
 * Two things only this function can get right, which is why the hub should call it rather than hand-rolling
 * the pair: the site sentence's layout clause has to defer to the pages when pages are present, and the
 * crops' sentence must not go out at all when the cap left no crop to talk about.
 *
 * Spread the result straight into `generateKitReference`.
 */
export function buildKitReferencePictures(args: {
  /** Page renders as data or http(s) URLs, in the order they should be read. */
  templatePages?: readonly string[]
  templateName?: string
  roleLabel?: string
  /** Site crops, already picked by `pickReferenceSections`. */
  siteCrops?: readonly string[]
  /** Required for the crops to ride: their sentence names the host, and a picture with no sentence is the
   *  exact failure `referenceImagesPromptBlock` exists to prevent. */
  host?: string
  family?: string
  max?: number
  /** `trace` is one page, copied one to one; see {@link TemplatePageMode}. Default `restyle`. */
  mode?: TemplatePageMode
  /** The page's measured size and its box on the stage. Required by `trace`, ignored by `restyle`. */
  trace?: TracePageFacts
}): { templatePage?: KitReferenceChannel; siteLook?: KitReferenceChannel } {
  const max = Math.max(0, args.max ?? MAX_REFERENCE_IMAGES)
  // A TRACE IS ONE PAGE AND ONLY ONE. Its instruction says "the attached image" and reads coordinates off
  // it; a second page in the array would be a second document's worth of geometry with one frame stated
  // for both, and the model has no way to know which of them the numbers describe.
  const pageCap = args.mode === 'trace' ? Math.min(1, max) : max
  const pages = (args.templatePages ?? [])
    .filter((u) => typeof u === 'string' && u.length > 0)
    .slice(0, pageCap)
  // NO SITE CROPS RIDE A TRACE. The crops' own sentence asks for a look to build in, which is composition
  // language, and a trace that has read it starts improving the page it was told to copy.
  const crops =
    args.mode === 'trace'
      ? []
      : (args.siteCrops ?? [])
          .filter((u) => typeof u === 'string' && u.length > 0)
          .slice(0, Math.max(0, max - pages.length))
  const host = args.host?.trim()
  const out: { templatePage?: KitReferenceChannel; siteLook?: KitReferenceChannel } = {}
  if (pages.length > 0) {
    out.templatePage = {
      referenceImages: [...pages],
      prompt: templatePagePrompt({
        pageCount: pages.length,
        siteCrops: host ? crops.length : 0,
        ...(args.templateName ? { templateName: args.templateName } : {}),
        ...(args.roleLabel ? { roleLabel: args.roleLabel } : {}),
        ...(args.mode ? { mode: args.mode } : {}),
        ...(args.trace ? { trace: args.trace } : {}),
      }),
    }
  }
  if (crops.length > 0 && host) {
    out.siteLook = {
      referenceImages: [...crops],
      prompt: siteLookPrompt(host, args.family, pages.length > 0 ? { templatePages: pages.length } : undefined),
    }
  }
  return out
}

/** Shared with `inlineMark`, which must compare URLs exactly as `stripUnlistedImages` does: a filter
 *  that keeps an asset the inline pass then cannot find leaves a live URL in a document rendered on
 *  an opaque origin, which draws as a broken-image icon. */
export const stripOrigin = (u: string): string => u.trim().replace(/^https?:\/\/[^/]+/i, '')

/**
 * The enforcement behind the closed list. Every `<img src="http…">` and every CSS `url(http…)` that is
 * not a listed asset goes; a `data-logo` declaration (no src yet) and `data:` URIs pass untouched, because
 * they are the contract's own doors. Compared without origin, so a listed asset written relative or
 * absolute is the same asset.
 */
export function stripUnlistedImages(html: string, allowed: Iterable<string>): string {
  const ok = new Set<string>()
  for (const u of allowed) ok.add(stripOrigin(u))
  const isAllowed = (u: string): boolean => ok.has(stripOrigin(u))

  let out = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const m = /\ssrc\s*=\s*(["'])(.*?)\1/i.exec(tag)
    if (!m) return tag
    const src = m[2].trim()
    if (!/^https?:\/\//i.test(src) && !src.startsWith('/')) return tag
    // A picture the image tool placed (data-deck-image, in storage) is the user's own and rides regardless
    // of this document's list: KIT ASSETS closes what the WRITER may add, not what the tool put there, and
    // this pass runs on every in-app rewrite of a document that may already hold one.
    if (/\sdata-deck-image\s*=/i.test(tag) && isStoredImageUrl(src)) return tag
    return isAllowed(src) ? tag.replace(/\ssrcset\s*=\s*(["']).*?\1/gi, '') : ''
  })
  out = out.replace(/url\(\s*(["']?)((?:https?:)?\/[^"')]+)\1\s*\)/gi, (whole, _q, u: string) =>
    isAllowed(u) ? whole : 'none'
  )
  return out
}
